import React from 'react';
import { 
  FiSearch,
  FiBarChart,
  FiMonitor,
  FiEdit,
  FiTrendingUp,
  FiMessageCircle,
  FiArrowRight,
  FiCheckCircle,
  FiDollarSign
} from 'react-icons/fi';

export const ServicesPage: React.FC = () => {
  const serviceCategories = [
    {
      id: 'ai-search',
      title: 'AI & Search Visibility',
      description: 'Advanced AI-powered SEO and search marketing solutions',
      icon: <FiSearch className="text-4xl" />,
      color: 'from-blue-500 to-blue-700',
      services: [
        {
          name: 'AI-Powered SEO',
          description: 'Machine learning-driven keyword research and content optimization',
          features: ['AI Content Optimization', 'Predictive Analytics', 'Automated Reporting', 'Voice Search Optimization'],
          pricing: 'Starting at ₹1,500/month',
          roi: 'Average 285% ROI'
        },
        {
          name: 'Technical SEO Audit',
          description: 'Comprehensive website analysis and optimization recommendations',
          features: ['Site Speed Analysis', 'Mobile Optimization', 'Schema Markup', 'Core Web Vitals'],
          pricing: 'Starting at ₹899',
          roi: 'Typical 40% traffic increase'
        },
        {
          name: 'Local SEO',
          description: 'Dominate local search results and attract nearby customers',
          features: ['Google My Business', 'Local Citations', 'Review Management', 'Local Link Building'],
          pricing: 'Starting at ₹799/month',
          roi: 'Average 200% local visibility boost'
        }
      ]
    },
    {
      id: 'digital-advertising',
      title: 'Digital Advertising',
      description: 'Data-driven PPC and paid advertising campaigns',
      icon: <FiBarChart className="text-4xl" />,
      color: 'from-green-500 to-green-700',
      services: [
        {
          name: 'Google Ads Management',
          description: 'Expert Google Ads campaign management and optimization',
          features: ['Campaign Setup', 'Keyword Research', 'Ad Copy Testing', 'Conversion Tracking'],
          pricing: 'Starting at ₹1,200/month',
          roi: 'Average 320% ROAS'
        },
        {
          name: 'Facebook & Instagram Ads',
          description: 'Social media advertising across Meta platforms',
          features: ['Audience Targeting', 'Creative Development', 'A/B Testing', 'Performance Analytics'],
          pricing: 'Starting at ₹999/month',
          roi: 'Average 280% ROAS'
        },
        {
          name: 'LinkedIn Advertising',
          description: 'B2B focused advertising campaigns on LinkedIn',
          features: ['Lead Generation', 'Account-Based Marketing', 'Sponsored Content', 'Message Ads'],
          pricing: 'Starting at ₹1,500/month',
          roi: 'Average 250% lead increase'
        }
      ]
    },
    {
      id: 'content-marketing',
      title: 'Content Marketing',
      description: 'Strategic content creation and distribution',
      icon: <FiEdit className="text-4xl" />,
      color: 'from-purple-500 to-purple-700',
      services: [
        {
          name: 'Content Strategy',
          description: 'Comprehensive content planning and editorial calendars',
          features: ['Content Audits', 'Editorial Calendars', 'Persona Development', 'Content Gaps Analysis'],
          pricing: 'Starting at ₹799/month',
          roi: 'Average 200% engagement increase'
        },
        {
          name: 'Blog Writing & SEO',
          description: 'SEO-optimized blog content that drives organic traffic',
          features: ['Keyword Research', 'SEO Writing', 'Content Promotion', 'Performance Tracking'],
          pricing: 'Starting at ₹299/article',
          roi: 'Average 180% traffic boost'
        },
        {
          name: 'Video Marketing',
          description: 'Professional video content creation and promotion',
          features: ['Video Production', 'YouTube Optimization', 'Video SEO', 'Social Video Ads'],
          pricing: 'Starting at ₹2,499/video',
          roi: 'Average 300% engagement rate'
        }
      ]
    },
    {
      id: 'web-development',
      title: 'Web Development',
      description: 'Custom websites and e-commerce solutions',
      icon: <FiMonitor className="text-4xl" />,
      color: 'from-orange-500 to-red-700',
      services: [
        {
          name: 'Custom Website Design',
          description: 'Bespoke website design and development',
          features: ['Responsive Design', 'CMS Integration', 'SEO Foundation', 'Performance Optimization'],
          pricing: 'Starting at ₹4,999',
          roi: 'Average 250% conversion increase'
        },
        {
          name: 'E-commerce Development',
          description: 'Complete online store solutions',
          features: ['Shopping Cart Setup', 'Payment Integration', 'Inventory Management', 'Mobile Optimization'],
          pricing: 'Starting at ₹7,999',
          roi: 'Average 300% sales increase'
        },
        {
          name: 'Website Maintenance',
          description: 'Ongoing website support and optimization',
          features: ['Security Updates', 'Performance Monitoring', 'Content Updates', 'Backup Management'],
          pricing: 'Starting at ₹199/month',
          roi: '99.9% uptime guarantee'
        }
      ]
    }
  ];

  const packages = [
    {
      name: 'Prime',
      price: 'From ₹9,999',
      period: 'per month',
      description: 'Ideal for businesses starting their digital journey',
      features: [
        'SEO with AI SEO from ₹19,999/mo',
        'Performance Marketing from ₹9,999/mo',
        'Social Media Management from ₹49,999/mo',
        'Core campaign setup & optimization',
        'Monthly performance reports',
        'Email support'
      ],
      popular: false,
      color: 'border-gray-200'
    },
    {
      name: 'Elite',
      price: 'From ₹19,999',
      period: 'per month',
      description: 'Comprehensive marketing for growing businesses',
      features: [
        'SEO with AI SEO from ₹29,999/mo',
        'Performance Marketing from ₹19,999/mo',
        'Social Media Management from ₹74,999/mo',
        'Multi-platform campaign management',
        'Advanced analytics & bi-weekly reports',
        'Dedicated account support',
        'A/B testing & conversion optimization'
      ],
      popular: true,
      color: 'border-primary-500'
    },
    {
      name: 'Supreme',
      price: 'From ₹29,999',
      period: 'per month',
      description: 'Full-scale digital domination for ambitious brands',
      features: [
        'SEO with AI SEO from ₹39,999/mo',
        'Performance Marketing from ₹29,999/mo',
        'Social Media Management from ₹99,999/mo',
        'Full-funnel multi-channel strategy',
        'Weekly reports & custom dashboards',
        'Dedicated account manager',
        'Priority support & monthly strategy calls',
        'Advance payment discounts up to 10%'
      ],
      popular: false,
      color: 'border-gray-200'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery & Analysis',
      description: 'We analyze your business, competitors, and market opportunities to create a data-driven strategy.',
      icon: <FiSearch />
    },
    {
      step: '02', 
      title: 'Strategy Development',
      description: 'Custom marketing strategy tailored to your goals, budget, and target audience.',
      icon: <FiTrendingUp />
    },
    {
      step: '03',
      title: 'Implementation',
      description: 'Expert execution of campaigns with continuous monitoring and optimization.',
      icon: <FiCheckCircle />
    },
    {
      step: '04',
      title: 'Results & Reporting',
      description: 'Transparent reporting with actionable insights and recommendations for growth.',
      icon: <FiBarChart />
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-32 bg-gradient-to-br from-primary-600 to-blue-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-6xl font-bold mb-8">
            Digital Marketing <span className="text-yellow-400">Services</span>
          </h1>
          <p className="text-2xl mb-12 max-w-4xl mx-auto opacity-90">
            Comprehensive digital marketing solutions designed to drive growth, 
            increase visibility, and maximize your ROI.
          </p>
          <div className="grid md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-400 mb-2">50+</div>
              <div className="text-lg">Services Offered</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-400 mb-2">98%</div>
              <div className="text-lg">Client Retention</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-400 mb-2">250%</div>
              <div className="text-lg">Average ROI</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-400 mb-2">24/7</div>
              <div className="text-lg">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our Service Categories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From AI-powered SEO to conversion-focused web development, we offer 
              comprehensive solutions to accelerate your digital growth.
            </p>
          </div>

          <div className="space-y-24">
            {serviceCategories.map((category, index) => (
              <div key={category.id} className="relative">
                {/* Category Header */}
                <div className={`bg-gradient-to-r ${category.color} text-white p-8 rounded-2xl mb-8`}>
                  <div className="flex items-center gap-6">
                    <div className="text-white/90">
                      {category.icon}
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold mb-2">{category.title}</h3>
                      <p className="text-xl opacity-90">{category.description}</p>
                    </div>
                  </div>
                </div>

                {/* Services Grid */}
                <div className="grid md:grid-cols-3 gap-8">
                  {category.services.map((service, serviceIndex) => (
                    <div key={serviceIndex} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300">
                      <h4 className="text-2xl font-bold text-gray-800 mb-4">{service.name}</h4>
                      <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                      
                      <div className="space-y-4 mb-6">
                        <h5 className="font-semibold text-gray-800">Key Features:</h5>
                        <ul className="space-y-2">
                          {service.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-center gap-2 text-gray-600">
                              <FiCheckCircle className="text-green-500 text-sm" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="border-t pt-6 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-2xl font-bold text-primary-600">{service.pricing}</span>
                          <span className="text-sm text-green-600 font-semibold">{service.roi}</span>
                        </div>
                        <button className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 group">
                          Learn More
                          <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Service Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the perfect package for your business needs. All packages include 
              our core services with varying levels of support and features.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => (
              <div key={index} className={`bg-white rounded-2xl shadow-lg border-2 ${pkg.color} p-8 relative ${pkg.popular ? 'transform scale-105' : ''}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    {pkg.price}
                    <span className="text-lg text-gray-600 font-normal">/{pkg.period}</span>
                  </div>
                  <p className="text-gray-600">{pkg.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <FiCheckCircle className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button className={`w-full py-4 rounded-lg font-semibold transition-all duration-300 ${
                  pkg.popular 
                    ? 'bg-primary-600 hover:bg-primary-700 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`}>
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our proven methodology ensures successful campaigns and measurable results 
              for every client we work with.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary-600 text-white w-20 h-20 rounded-full flex items-center justify-center text-2xl mx-auto mb-6">
                  {step.icon}
                </div>
                <div className="bg-primary-100 text-primary-800 text-2xl font-bold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Accelerate Your Growth?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Let's discuss your goals and create a custom strategy to drive real results. 
            Get started with a free consultation today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg flex items-center gap-2">
              <FiMessageCircle />
              Free Consultation
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all duration-300 flex items-center gap-2">
              <FiDollarSign />
              View Pricing
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};